import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronDown, Film, Tv, Mic, User, Tag, LayoutDashboard, Shield, Settings, CreditCard, LogOut, Users, Play, Check } from 'lucide-react';
import Logo from './Logo';
import { useApp } from '../context/AppContext';
import { useSearch } from '../lib/api';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/movies', label: 'Movies' },
  { to: '/shows', label: 'Shows' },
  { to: '/podcasts', label: 'Podcasts' },
  { to: '/live', label: 'Live' },
  { to: '/originals', label: 'Originals' },
  { to: '/my-list', label: 'My List' },
];

function SearchBox() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { openDetail } = useApp();
  const ref = useRef(null);
  const { res: rawRes, loading } = useSearch(q);
  const res = q.trim() ? rawRes : null;
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const go = (id) => { setOpen(false); setQ(''); openDetail(id); };
  const total = res ? res.movies.length + res.shows.length + res.podcasts.length + res.episodes.length + res.people.length + res.genres.length : 0;
  return (
    <div ref={ref} className="relative w-[200px] xl:w-[312px] hidden md:block" data-testid="header-search">
      <form
        onSubmit={(e) => { e.preventDefault(); if (q.trim()) { setOpen(false); navigate(`/search?q=${encodeURIComponent(q)}`); } }}
        className="search-pill flex items-center gap-3 px-4"
      >
        <Search size={17} className="text-[#C7C4CB]" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search for movies, shows, podcasts..."
          className="bg-transparent outline-none text-[13px] text-[#FFFFFF] placeholder:text-[#8A8694] w-full"
          data-testid="header-search-input"
        />
      </form>
      {open && res && (
        <div className="absolute top-[48px] left-0 right-0 surface shadow-xl shadow-[#000000]/10 p-2 z-[80] max-h-[70vh] overflow-y-auto fade-in" data-testid="search-results-panel">
          {total === 0 && !loading && <div className="px-3 py-4 text-sm text-[#C7C4CB]">No results for "{q}"</div>}
          {total === 0 && loading && <div className="px-3 py-4 text-sm text-[#C7C4CB]">Searching...</div>}
          {[['Movies', res.movies, Film], ['Shows', res.shows, Tv], ['Podcasts', res.podcasts, Mic]].map(([label, list, Icon]) => list.length > 0 && (
            <div key={label} className="mb-1">
              <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wider text-[#8A8694] font-semibold">{label}</div>
              {list.slice(0, 4).map((c) => (
                <button key={c.id} onClick={() => go(c.id)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] text-left">
                  <img src={c.image} alt="" className="w-14 h-8 object-cover rounded" />
                  <div className="min-w-0"><div className="text-[13px] font-medium truncate">{c.title}</div><div className="text-[11px] text-[#8A8694]">{c.genres?.slice(0, 2).join(' \u2022 ')} {c.year && `\u2022 ${c.year}`}</div></div>
                  <Icon size={14} className="ml-auto text-[#8A8694]" />
                </button>
              ))}
            </div>
          ))}
          {res.episodes.length > 0 && (
            <div className="mb-1">
              <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wider text-[#8A8694] font-semibold">Episodes</div>
              {res.episodes.map((e) => (
                <button key={e.ep.id} onClick={() => go((e.show || e.podcast).id)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] text-left">
                  <Play size={14} className="text-[#C7C4CB]" />
                  <div className="min-w-0"><div className="text-[13px] truncate">{e.ep.title}</div><div className="text-[11px] text-[#8A8694]">{(e.show || e.podcast).title}{e.season ? ` \u2022 S${e.season} E${e.ep.number}` : ''}</div></div>
                </button>
              ))}
            </div>
          )}
          {(res.people.length > 0 || res.genres.length > 0) && (
            <div className="flex flex-wrap gap-1.5 px-3 py-2">
              {res.people.map((p) => <button key={p} onClick={() => { setOpen(false); navigate(`/search?q=${encodeURIComponent(p)}`); }} className="chip h-7 text-[12px] gap-1.5"><User size={12} />{p}</button>)}
              {res.genres.map((g) => <button key={g} onClick={() => { setOpen(false); navigate(`/search?q=${encodeURIComponent(g)}`); }} className="chip h-7 text-[12px] gap-1.5"><Tag size={12} />{g}</button>)}
            </div>
          )}
          <button onClick={() => { setOpen(false); navigate(`/search?q=${encodeURIComponent(q)}`); }} className="w-full text-center text-[12px] text-[#C7C4CB] hover:text-[#FFFFFF] py-2 border-t border-[#26212F] mt-1">See all results</button>
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const { notifs, unreadCount, markAllRead, openDetail, user } = useApp();
  if (!user) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative text-[#C7C4CB] hover:text-[#FFFFFF] transition-colors" aria-label="Notifications" data-testid="notifications-button">
          <Bell size={22} strokeWidth={1.8} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-[3px] rounded-full bg-[#8B2CFF] text-white text-[9px] font-bold flex items-center justify-center border-2 border-[#0B0A0E]">{unreadCount}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 bg-[#15121B] border-[#26212F] text-[#FFFFFF] shadow-xl shadow-[#000000]/10" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#26212F]">
          <span className="font-semibold text-sm">Notifications</span>
          <button onClick={markAllRead} className="text-[12px] text-[#C7C4CB] hover:text-[#FFFFFF]">Mark all as read</button>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {notifs.length === 0 && <div className="px-4 py-6 text-[13px] text-[#8A8694] text-center">You're all caught up.</div>}
          {notifs.map((n) => (
            <button key={n.id} onClick={() => { markAllRead(); if (n.contentId) openDetail(n.contentId); }} className="w-full flex gap-3 px-4 py-3 hover:bg-white/[0.04] text-left">
              {n.image ? <img src={n.image} alt="" className="w-16 h-10 rounded object-cover" /> : <span className="w-16 h-10 rounded bg-[#8B2CFF]/20 flex items-center justify-center"><Bell size={14} className="text-[#8B2CFF]" /></span>}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium flex items-center gap-2">{n.title}{n.unread && <span className="w-1.5 h-1.5 rounded-full bg-[#8B2CFF]" />}</div>
                <div className="text-[12px] text-[#C7C4CB] truncate">{n.text}</div>
                <div className="text-[11px] text-[#8A8694] mt-0.5">{n.time}</div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ProfileMenu() {
  const { user, activeProfile, profiles, setActiveProfileId, signOut, isAdmin, isCreator, authLoading } = useApp();
  const navigate = useNavigate();
  if (!user) {
    if (authLoading) return <div className="w-9 h-9 rounded-full bg-white/[0.06] animate-pulse" />;
    return <Link to="/signin" className="btn-brand !h-9 !px-4 text-[13px]" data-testid="header-signin">Sign In</Link>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 group" data-testid="profile-menu-button">
          <img src={activeProfile?.avatar || user?.avatar} alt="" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/15 group-hover:ring-[#8B2CFF]/60 transition" />
          <ChevronDown size={18} className="text-[#C7C4CB] group-hover:text-[#FFFFFF] transition" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[240px] bg-[#15121B] border-[#26212F] text-[#FFFFFF] shadow-xl shadow-[#000000]/10" data-testid="profile-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-[#8A8694]">Profiles</DropdownMenuLabel>
        {profiles.map((p) => (
          <DropdownMenuItem key={p.id} onClick={() => setActiveProfileId(p.id)} className="gap-3 focus:bg-[#2A2136] focus:text-[#FFFFFF] cursor-pointer">
            <img src={p.avatar} alt="" className="w-7 h-7 rounded object-cover" />
            <span className="flex-1 text-[13px]">{p.name}</span>
            {p.id === activeProfile?.id && <Check size={14} className="text-[#8B2CFF]" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/[0.06]" />
        {[['Manage Profiles', Users, '/profiles'], ['Account', Settings, '/account'], ['Subscription', CreditCard, '/account?tab=subscription'], ['Playback Settings', Play, '/account?tab=playback']].map(([l, I, to]) => (
          <DropdownMenuItem key={l} onClick={() => navigate(to)} className="gap-3 text-[13px] focus:bg-[#2A2136] focus:text-[#FFFFFF] cursor-pointer"><I size={15} className="text-[#C7C4CB]" />{l}</DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/[0.06]" />
        {isCreator && <DropdownMenuItem onClick={() => navigate('/studio')} className="gap-3 text-[13px] focus:bg-[#2A2136] focus:text-[#FFFFFF] cursor-pointer" data-testid="menu-studio"><LayoutDashboard size={15} className="text-[#C7C4CB]" />Creator Studio</DropdownMenuItem>}
        {isAdmin && <DropdownMenuItem onClick={() => navigate('/admin')} className="gap-3 text-[13px] focus:bg-[#2A2136] focus:text-[#FFFFFF] cursor-pointer" data-testid="menu-admin"><Shield size={15} className="text-[#C7C4CB]" />Clax Admin</DropdownMenuItem>}
        {(isCreator || isAdmin) && <DropdownMenuSeparator className="bg-white/[0.06]" />}
        <DropdownMenuItem onClick={() => { signOut(); navigate('/signin'); }} className="gap-3 text-[13px] focus:bg-[#2A2136] focus:text-[#FFFFFF] cursor-pointer" data-testid="sign-out"><LogOut size={15} className="text-[#C7C4CB]" />Sign Out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function TopNav() {
  const { pathname } = useLocation();
  return (
    <header className="top-nav fixed top-0 left-0 right-0 z-[60] h-[70px]" data-testid="top-nav">
      <div className="h-full flex items-center pl-[38px] pr-[24px]">
        <Link to="/" className="flex items-center" data-testid="logo-link"><Logo height={42} /></Link>
        <nav className="hidden lg:flex items-center gap-[24px] xl:gap-[40px] ml-[36px] xl:ml-[70px]">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav-link ${isActive || (n.to !== '/' && pathname.startsWith(n.to)) ? 'active' : ''}`} data-testid={`nav-${n.label.toLowerCase().replace(' ', '-')}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-[22px] xl:gap-[34px]">
          <SearchBox />
          <Link to="/search" className="md:hidden text-[#C7C4CB]"><Search size={22} /></Link>
          <Notifications />
          <ProfileMenu />
        </div>
      </div>
    </header>
  );
}
